import { useId, type ReactNode } from 'react';

import { Icon, type IconName } from '@eliadmin/core/src/components/nt/ui/icon';
import { cn } from '@eliadmin/core/src/utils/style-utils';

/**
 * Figma „Empty state“ (guest favourites 208:12680): #F5F6F3 card, radius 16, padding 56 / 48
 * (32 / 16 on phones), 24 px gaps, centred 44 px icon in green, the H3 title over a Body/L #363D36
 * text (max 640 px, 8 px apart), then the actions and the extras. Used for the guest prompt (title =
 * the page's h1), the empty list and the load error. No hooks – it renders on the server too.
 */
export function FavoritesStateCard({
  icon,
  title,
  titleAs: Title = 'h2',
  text,
  actions,
  className,
  children,
}: {
  icon: IconName;
  title: ReactNode;
  titleAs?: 'h1' | 'h2';
  text?: ReactNode;
  actions?: ReactNode;
  className?: string;
  children?: ReactNode;
}) {
  const titleId = useId();

  return (
    <section
      aria-labelledby={titleId}
      className={cn(
        'flex flex-col items-center gap-6 rounded-2xl bg-[#F5F6F3] px-4 py-8 text-center sm:px-12 sm:py-14',
        className,
      )}
    >
      <Icon name={icon} size={44} className="text-[#127A26]" />
      <div className="flex w-full max-w-[640px] flex-col gap-2">
        <Title id={titleId} className="nt-h3 text-[#0D110D]">
          {title}
        </Title>
        {text ? <p className="nt-body-l text-[#363D36]">{text}</p> : null}
      </div>
      {actions ? (
        <div className="flex w-full flex-col justify-center gap-3 sm:w-auto sm:flex-row">
          {actions}
        </div>
      ) : null}
      {children}
    </section>
  );
}
